import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import ResultsView from "@/components/ResultsView";
import { LEAD } from "@/constants/testIds";

export default function Results() {
  const navigate = useNavigate();
  const [answers, setAnswers] = useState(null);
  const [stage, setStage] = useState("lead"); // lead | submitting | results
  const [form, setForm] = useState({ name: "", email: "", company: "" });
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [shareUrl, setShareUrl] = useState("");

  useEffect(() => {
    const raw = sessionStorage.getItem("quiz_answers");
    if (!raw) {
      navigate("/quiz");
      return;
    }
    const parsed = JSON.parse(raw);
    if (Object.keys(parsed).length < 5) {
      navigate("/quiz");
      return;
    }
    setAnswers(parsed);
  }, [navigate]);

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim()) {
      setError("Name and email are required.");
      return;
    }
    setStage("submitting");
    try {
      const { data } = await api.post("/leads", {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        answers,
      });
      setResult({ ...data, first_name: form.name.trim().split(" ")[0] });
      setShareUrl(`${window.location.origin}/api/r/${data.lead_id}`);
      setStage("results");
      // Update the URL so refresh keeps results (no navigation reload needed)
      window.history.replaceState({}, "", `/r/${data.lead_id}`);
    } catch (e2) {
      setError(formatApiError(e2.response?.data?.detail) || e2.message);
      setStage("lead");
    }
  }

  if (!answers) return null;

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SiteHeader />
      <main className="flex-1 max-w-5xl w-full mx-auto px-5 sm:px-6 py-10 lg:py-16">
        {stage !== "results" && (
          <div className="max-w-xl mx-auto fade-in-up">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#0f766e]">
              One last step
            </span>
            <h1 className="font-display mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
              Where should we send your matches?
            </h1>
            <p className="mt-3 text-slate-600">
              We'll show your 3 recommended tools on the next screen and save a copy
              you can revisit or share.
            </p>

            <form onSubmit={submit} data-testid={LEAD.form} className="mt-8 space-y-5">
              <div>
                <Label htmlFor="name" className="text-slate-700">Your name</Label>
                <Input
                  id="name"
                  data-testid={LEAD.nameInput}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Jane Doe"
                  className="mt-1.5 h-11 border-slate-200 focus-visible:ring-[#0f766e] focus-visible:border-[#0f766e]"
                  required
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-slate-700">Work email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid={LEAD.emailInput}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@company.com"
                  className="mt-1.5 h-11 border-slate-200 focus-visible:ring-[#0f766e] focus-visible:border-[#0f766e]"
                  required
                />
              </div>
              <div>
                <Label htmlFor="company" className="text-slate-700">
                  Company <span className="text-slate-400 text-sm">(optional)</span>
                </Label>
                <Input
                  id="company"
                  data-testid={LEAD.companyInput}
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  placeholder="Acme Inc."
                  className="mt-1.5 h-11 border-slate-200 focus-visible:ring-[#0f766e] focus-visible:border-[#0f766e]"
                />
              </div>

              <div className="flex items-center gap-2 text-xs text-slate-500">
                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                We won't share your data. Unsubscribe any time.
              </div>

              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md p-3">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={stage === "submitting"}
                data-testid={LEAD.submitBtn}
                className="w-full h-12 bg-[#0f766e] hover:bg-[#115e59] text-white text-base"
              >
                {stage === "submitting" ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating your matches…
                  </>
                ) : (
                  "Show me my matches"
                )}
              </Button>
            </form>
          </div>
        )}

        {stage === "results" && result && (
          <ResultsView
            blurb={result.blurb}
            tools={result.tools}
            firstName={result.first_name}
            shareUrl={shareUrl}
            leadId={result.lead_id}
            sourceRef={null}
          />
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
